/**
 * The card layout editor. CRM-BUILD item 30, writing item 29's layout.
 *
 * ┌─ ⛔ IT SHIPS WITH THE PIPELINE BOARD RATHER THAN AFTER IT ──────────────────────────┐
 * │ Amir 2026-09-15: *"a board whose cards show a fixed set is the board before  │
 * │ the editor, and shipping it twice costs an inspection round."*              │
 * │                                                                            │
 * │ ⚠️ AND ONE QUARTER OF ITEM 29 WAS ALREADY BUILT: the per-stage count header  │
 * │ strip shipped with the board on 2026-09-15. What was missing is this.       │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ REORDER IS BUTTONS, NOT DRAG, AND THAT IS AMIR'S OWN RULING APPLIED EARLY.
 * His finding 4 on the contacts list was that UP and DOWN arrows are wrong for a
 * left-to-right layout. **A card's slots stack vertically**, so up and down are
 * the right words here - the ruling was about matching the axis, not about
 * banning the control.
 *
 * ⚠️ UNGATED, LIKE THE PIPELINE SELECTOR. Item 29 says admin-configurable and
 * the permission model is ADMIN-ANALYTICS's and does not exist. Stated rather
 * than faked.
 */
import { useEffect, useRef, useState } from 'react'
import { saveCardLayout, type Board } from '../data/bookings-api.js'

type Slot = Board['cardLayout'][number]

export function CardLayoutEditor({
  layout,
  pipelineId,
  pipelineLabel,
  onClose,
  onSaved,
}: {
  layout: Slot[]
  pipelineId: string
  pipelineLabel: string
  onClose: () => void
  onSaved: () => void
}) {
  const [slots, setSlots] = useState<Slot[]>(layout)
  const [applyToAll, setApplyToAll] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const first = useRef<HTMLButtonElement | null>(null)

  /** ⚠️ FOCUS ENTERS THE DIALOG, which is `ConfirmDialog`'s own rule: a modal
   *  that opens behind the keyboard's position is a modal a keyboard user has to
   *  hunt for. */
  useEffect(() => {
    first.current?.focus()
  }, [])

  const swap = (index: number, delta: number) => {
    const to = index + delta
    if (to < 0 || to >= slots.length) return
    const next = slots.slice()
    const a = next[index]!
    const b = next[to]!
    next[index] = b
    next[to] = a
    setSlots(next)
  }

  const save = async () => {
    setSaving(true)
    setFailed(false)
    const ok = await saveCardLayout({
      pipelineId,
      slots: slots.map((s) => ({ key: s.key, visible: s.visible })),
      applyToAll,
    })
    setSaving(false)
    if (ok) onSaved()
    else setFailed(true)
  }

  return (
    <div
      className="board__editor-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Configure Cards"
      data-testid="card-layout-editor"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className="board__editor">
        <h2 className="board__editor-title">Configure Cards</h2>
        <p className="board__editor-note">
          What every card on this board shows, and in what order. This is an account setting: everyone
          sees the same cards.
        </p>

        <ul className="board__editor-slots">
          {slots.map((slot, index) => (
            <li key={slot.key} className="board__editor-slot" data-slot={slot.key}>
              <label className="board__editor-toggle">
                <input
                  type="checkbox"
                  checked={slot.visible}
                  /** ⛔ THE TITLE CANNOT BE TURNED OFF. A card with nothing on it
                   *  is not a configuration, it is a blank rectangle that cannot
                   *  be clicked back into existence. The server enforces it too. */
                  disabled={slot.required}
                  data-testid={`slot-visible-${slot.key}`}
                  onChange={(e) =>
                    setSlots(
                      slots.map((s) => (s.key === slot.key ? { ...s, visible: e.target.checked } : s)),
                    )
                  }
                />
                {slot.label}
                {slot.required && <span className="board__editor-required"> (always shown)</span>}
              </label>
              <span className="board__editor-move">
                <button
                  type="button"
                  ref={index === 0 ? first : undefined}
                  disabled={index === 0}
                  aria-label={`Move ${slot.label} Up`}
                  data-testid={`slot-up-${slot.key}`}
                  onClick={() => swap(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={index === slots.length - 1}
                  aria-label={`Move ${slot.label} Down`}
                  data-testid={`slot-down-${slot.key}`}
                  onClick={() => swap(index, 1)}
                >
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ul>

        {/**
         * ⛔ ITEM 29's APPLY-TO-ALL-PIPELINES OPTION, and it says what it does to
         * the other pipelines rather than only what it does to this one. Ticking
         * it DELETES every per-pipeline layout, because "apply to all pipelines"
         * that left overrides in place would be "apply to all pipelines that
         * have not been configured" - the same button with a quieter promise.
         */}
        <label className="board__editor-all">
          <input
            type="checkbox"
            checked={applyToAll}
            data-testid="slot-apply-all"
            onChange={(e) => setApplyToAll(e.target.checked)}
          />
          Use this layout for every pipeline
          <span className="board__editor-note">
            {applyToAll
              ? 'Any layout set on another pipeline will be replaced by this one.'
              : `This layout applies to ${pipelineLabel} only.`}
          </span>
        </label>

        {failed && (
          <p className="board__editor-error" role="alert">
            The layout could not be saved.
          </p>
        )}

        <div className="board__editor-actions">
          <button type="button" onClick={onClose} data-testid="slot-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="board__editor-save"
            disabled={saving}
            data-testid="slot-save"
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
